import { useEffect, useRef, useState } from "react";

/**
 * S9b T13 — Cloudflare Turnstile widget for landing forms.
 * (Mirror of apps/web/src/components/TurnstileWidget.tsx — kept separate so
 * landing has no dependency on web's component graph.)
 */

type Props = {
  onToken: (token: string) => void;
  theme?: "light" | "dark" | "auto";
  testBypass?: boolean;
};

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        opts: {
          sitekey: string;
          action?: string;
          theme?: string;
          callback?: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
        },
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

function getSiteKey(): string {
  return (
    (import.meta as unknown as { env?: Record<string, string> }).env
      ?.VITE_TURNSTILE_SITE_KEY ?? ""
  );
}

export function TurnstileWidget({ onToken, theme = "light", testBypass }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const siteKey = getSiteKey();

  useEffect(() => {
    if (testBypass) {
      onToken("test-token");
      return;
    }
    if (!siteKey) {
      onToken("");
      return;
    }
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (!existing) {
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }

    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (!window.turnstile || !ref.current) {
        setTimeout(tryRender, 200);
        return;
      }
      try {
        widgetIdRef.current = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          action: "turnstile-spin-v1",
          theme,
          callback: (token: string) => onToken(token),
          "error-callback": () => setError("Verification failed. Please retry."),
          "expired-callback": () => onToken(""),
        });
      } catch (e) {
        setError((e as Error).message);
      }
    };
    tryRender();
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
      }
    };
  }, [onToken, theme, siteKey, testBypass]);

  if (testBypass) return null;
  if (!siteKey) {
    return (
      <div className="text-xs text-ink-500" data-testid="turnstile-disabled">
        (CAPTCHA not configured — dev mode)
      </div>
    );
  }

  return (
    <div>
      <div ref={ref} data-testid="turnstile-widget" />
      {error && (
        <p role="alert" className="text-xs text-red-600 mt-1">
          {error}
        </p>
      )}
    </div>
  );
}
